import React from 'react';

// Function to handle changes in date transformations and thresholds
export const handleDateTransformationChange = (fieldName, type, value, setFieldsConfig) => {
    setFieldsConfig(prevFieldsConfig => {
        const updatedFieldsConfig = { ...prevFieldsConfig };
        const dateTransformations = updatedFieldsConfig[fieldName]?.dateTransformations || {
            format: '',
            arithmetic: { value: '', unit: '' },
            extract: '',
            threshold: ''
        };

        if (type === 'arithmetic') {
            dateTransformations.arithmetic.value = value;
        } else if (type === 'unit') {
            dateTransformations.arithmetic.unit = value;
        } else if (type === 'threshold') {
            dateTransformations.threshold = value;
        } else {
            dateTransformations[type] = value;
        }

        updatedFieldsConfig[fieldName] = { ...updatedFieldsConfig[fieldName], dateTransformations };
        return updatedFieldsConfig;
    });
};

// Component to render the date format input fields
export const FormatConversionSection = ({ fieldName, fieldsConfig, handleDateTransformationChange }) => (
    <div className="date-transformation-section">
        <label>Date Format:</label>
        <select
            value={fieldsConfig[fieldName]?.dateTransformations?.format || ''}
            onChange={(e) => handleDateTransformationChange(fieldName, 'format', e.target.value)}
        >
            <option value="">Select Format</option>
            {/* Common Date Formats */}
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            <option value="YYYY/MM/DD">YYYY/MM/DD</option>
            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
            <option value="MM-DD-YYYY">MM-DD-YYYY</option>
            <option value="YYYY.MM.DD">YYYY.MM.DD</option>
            <option value="DD.MM.YYYY">DD.MM.YYYY</option>
            <option value="MM.DD.YYYY">MM.DD.YYYY</option>

            {/* No Delimiter Date Formats */}
            <option value="YYYYMMDD">YYYYMMDD</option>
            <option value="DDMMYYYY">DDMMYYYY</option>
            <option value="MMDDYYYY">MMDDYYYY</option>

            {/* Common Datetime Formats */}
            <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
            <option value="YYYY/MM/DD HH:mm:ss">YYYY/MM/DD HH:mm:ss</option>
            <option value="DD-MM-YYYY HH:mm:ss">DD-MM-YYYY HH:mm:ss</option>
            <option value="MM-DD-YYYY HH:mm:ss">MM-DD-YYYY HH:mm:ss</option>
            <option value="YYYY.MM.DD HH:mm:ss">YYYY.MM.DD HH:mm:ss</option>
            <option value="DD.MM.YYYY HH:mm:ss">DD.MM.YYYY HH:mm:ss</option>
            <option value="MM.DD.YYYY HH:mm:ss">MM.DD.YYYY HH:mm:ss</option>

            {/* No Delimiter Datetime Formats */}
            <option value="YYYYMMDDHHmmss">YYYYMMDDHHmmss</option>
            <option value="DDMMYYYYHHmmss">DDMMYYYYHHmmss</option>
            <option value="MMDDYYYYHHmmss">MMDDYYYYHHmmss</option>

            {/* ISO and RFC Formats */}
            <option value="YYYY-MM-DDTHH:mm:ss">YYYY-MM-DDTHH:mm:ss (ISO 8601)</option>
            <option value="YYYY-MM-DDTHH:mm:ssZ">YYYY-MM-DDTHH:mm:ssZ (ISO 8601 UTC)</option>
            <option value="ddd, DD MMM YYYY HH:mm:ss GMT">ddd, DD MMM YYYY HH:mm:ss GMT (RFC 822/1123)</option>
            <option value="YYYY-MM-DDTHH:mm:ss.SSSZ">YYYY-MM-DDTHH:mm:ss.SSSZ (ISO 8601 UTC with milliseconds)</option>


            {/* Other Date Formats */}
            <option value="MMM DD, YYYY">MMM DD, YYYY</option>
            <option value="MMMM DD, YYYY">MMMM DD, YYYY</option>
            <option value="DD MMM YYYY">DD MMM YYYY</option>
            <option value="DD MMMM YYYY">DD MMMM YYYY</option>
            <option value="MMM DD, YYYY HH:mm:ss">MMM DD, YYYY HH:mm:ss</option>
            <option value="MMMM DD, YYYY HH:mm:ss">MMMM DD, YYYY HH:mm:ss</option>
            <option value="DD MMM YYYY HH:mm:ss">DD MMM YYYY HH:mm:ss</option>
            <option value="DD MMMM YYYY HH:mm:ss">DD MMMM YYYY HH:mm:ss</option>

            {/* Time-only Formats */}
            <option value="HH:mm:ss">HH:mm:ss</option>
            <option value="HH:mm:ss.SSS">HH:mm:ss.SSS</option>
            <option value="hh:mm A">hh:mm A</option>
        </select>
    </div>
);




// Component to render the date arithmetic input fields
export const DateArithmeticSection = ({ fieldName, fieldsConfig, handleDateTransformationChange }) => (
    <div className="date-transformation-section">
        <label>Add/Subtract:</label>
        <input
            type="number"
            value={fieldsConfig[fieldName]?.dateTransformations?.arithmetic?.value || ''}
            onChange={(e) => handleDateTransformationChange(fieldName, 'arithmetic', e.target.value)}
        />
        <label>Unit:</label>
        <select
            value={fieldsConfig[fieldName]?.dateTransformations?.arithmetic?.unit || ''}
            onChange={(e) => handleDateTransformationChange(fieldName, 'unit', e.target.value)}
        >
            <option value="">Select Unit</option>
            <option value="days">Days</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
        </select>
    </div>
);

// Component to render the extract date component input fields
export const ExtractDateComponentSection = ({ fieldName, fieldsConfig, handleDateTransformationChange }) => (
    <div className="date-transformation-section">
        <label>Extract:</label>
        <select
            value={fieldsConfig[fieldName]?.dateTransformations?.extract || ''}
            onChange={(e) => handleDateTransformationChange(fieldName, 'extract', e.target.value)}
        >
            <option value="">Select Component</option>
            <option value="year">Year</option>
            <option value="month">Month</option>
            <option value="day">Day</option>
            <option value="weekday">Weekday</option>
        </select>
    </div>
);

// Component to render the date thresholds input fields
export const DateThresholdsSection = ({ fieldName, fieldsConfig, handleDateTransformationChange, setFieldsConfig }) => (
    <div className="date-thresholds-section">
        <div className="date-threshold-item">
            <label>Threshold:</label>
            <input
                type="text"
                value={fieldsConfig[fieldName]?.dateTransformations?.threshold || ''}
                placeholder="Enter threshold"
                onChange={(e) => handleDateTransformationChange(fieldName, 'threshold', e.target.value, setFieldsConfig)}
            />
        </div>
    </div>
);
